import { useState } from 'react'

import {
  useLazyListEntitiesQuery,
  useGetOneFatEntityQuery,
  useUpsertEntityMutation,
  useBulkUpdateEntitiesMutation,
  useLazyListEntityTypesQuery,
  useDeleteEntityMutation,
} from '../../apiSlice/entityApi'

import { useBulkUpdateDatazonesMutation } from '../../apiSlice/datazoneApi'
import { useBulkUpdateAssetsMutation } from '../../apiSlice/assetApi'

import { getPushPull } from '../../apiSlice/pushPullHelper'

import useSearchCallback from '../../apiSlice/useSearchCallback'
import useFetchAuxiliaryLists from '../../apiSlice/useFetchAuxiliaryLists'
import { BucketConfig } from '../../../sharedcomponents/5templates/detailCards/BucketConfig'
import {
  ArtificialDetailPropertyNames,
  RootObjectTypes,
} from '../../../common/globalNotary'
import { useBulkUpdateContractsMutation } from '../../apiSlice/contractApi'
import { difference } from 'lodash'
import { useNavigate } from 'react-router-dom'

const useEntity = () => {
  const [loadEntities] = useLazyListEntitiesQuery()
  const [deleteEntity] = useDeleteEntityMutation()
  const [bulkUpdateEntitiesCallBack] = useBulkUpdateEntitiesMutation()
  const [bulkUpdateAssetsCallBack] = useBulkUpdateAssetsMutation()
  const [upsertEntityRecordCallback] = useUpsertEntityMutation()
  const [bulkUpdateDatazones] = useBulkUpdateDatazonesMutation()
  const [bulkUpdateContracts] = useBulkUpdateContractsMutation()
  const [loadEntityTypes] = useLazyListEntityTypesQuery()
  const navigate = useNavigate()

  const {
    searchDatazonesCallback,
    searchPartiesCallback,
    searchGroupsCallback,
    searchContractsCallback,
    searchNonContainerAssetsCallback,
    searchServicesCallback,
  } = useSearchCallback()

  const [finalAuxiliaryRecords, setFinalAuxiliaryRecords] = useState(null)
  const [loading, setLoading] = useState(false);

  const constructAndSetFinalListBucket = (completeResponse, primaryEntityRecord) => {
    const finalListBuckets = {}

    finalListBuckets.primero = [
      new BucketConfig(
        completeResponse[0].data,
        searchPartiesCallback,
        'Contacts',
        null,
        'genericSelect',
        'decorated',
        {
          controlArray: [
            {
              type: 'select',
              baseName: 'fullContacts[INDEX].role_id',
              optionsList: completeResponse[8].data
                .map((o) => {
                  o.value = o.id
                  o.text = o.name
                  return o
                })
                .filter((o) => o.type === 'contact'),
              disabled: false,
            },
          ],
        }
      ),
      new BucketConfig(
        completeResponse[1].data,
        searchPartiesCallback,
        'Owners',
        null,
        'genericSelect',
        'decorated',
        {
          controlArray: [
            {
              type: 'select',
              baseName: 'fullOwners[INDEX].role_id',
              optionsList: completeResponse[8].data
                .map((o) => {
                  o.value = o.id
                  o.text = o.name
                  return o
                })
                .filter((o) => o.type === 'owner'),
              disabled: false,
            },
          ],
        }
      ),
    ]

    finalListBuckets.segundo = [
      new BucketConfig(
        completeResponse[9].data,
        searchContractsCallback,
        'Accounts',
        null
      ),
      new BucketConfig(
        completeResponse[2].data,
        searchNonContainerAssetsCallback,
        'Locations',
        null,
        undefined,
        undefined,
        {
          restrictionMessage: 'Locations must always have an entity assigned. To change a location\'s entity, edit Entity Name in the location editor.',
        },
        true
      ),
      new BucketConfig(
        completeResponse[3].data,
        searchServicesCallback,
        'Services',
        null
      ),
    ]

    finalListBuckets.tercero = [
      new BucketConfig(
        completeResponse[4].data,
        searchGroupsCallback,
        'Operational Groups',
        { type: 'operational' },
        undefined,
        undefined,
        undefined,
        undefined,
        true // is read only
      ),
      new BucketConfig(
        completeResponse[5].data,
        searchDatazonesCallback,
        'Commercial Zones',
        { type: 'commercial' },
      ),
    ]

    finalListBuckets.entityTypes = completeResponse[6].data
    finalListBuckets.industryTypes = completeResponse[7].data

    setFinalAuxiliaryRecords(finalListBuckets)
  }

  const {
    constructServicesByIdArrayCall,
    constructValueAsPromiseCall,
    constructGetAllEntityTypesCall,
    constructGetAllIndustriesCall,
    constructGetAllEntityRolesCall,
  } = useFetchAuxiliaryLists()

  const fetchAuxiliaryRecords = async (primaryEntityRecord) => {
    const equalityEvaluator = (a, b) => {
      return a.id === b.id && a.base_type === b.base_type
    }

    const fetchCallArray = [
      constructValueAsPromiseCall(
        'fullContacts',
        primaryEntityRecord.fullContacts,
        ['role_id'],
        equalityEvaluator
      ),
      constructValueAsPromiseCall(
        'fullOwners',
        primaryEntityRecord.fullOwners,
        ['role_id'],
        equalityEvaluator
      ),

      ///assets
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3,
        primaryEntityRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3]
      ),

      // services
      constructServicesByIdArrayCall(primaryEntityRecord, 'service_ids'),

      ///groups
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1,
        primaryEntityRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1]
      ),

      ///datazones
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2,
        primaryEntityRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2]
      ),

      constructGetAllEntityTypesCall(),
      constructGetAllIndustriesCall(),
      constructGetAllEntityRolesCall(),

      // Contracts
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4,
        primaryEntityRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4]
      ),
    ]

    /// this Promise.all will not reject, errors come back on the error object,
    /// if everything works out, the error object is null or undefined
    Promise.all(fetchCallArray.map((f) => f.call()))
      .then((res) => {
        constructAndSetFinalListBucket(res, primaryEntityRecord)
      })
      .catch((error) => {
        console.error('Error fetching auxiliary records:', error)
      })
  }

  const listEntityTypes = async () => {
    const {
      data: { data: types } = { data: [] },
    } = await loadEntityTypes()

    return types
  }

  const removeEntity = async id => {
    deleteEntity({ id }).then(() => {
      navigate(-1)
    }).catch(err => {
      console.error(err)
      alert('Could not delete entity. Please check that other objects do not depend on it.')
    })
  }

  const suspendEntity = async (id, active) => {
    bulkUpdateEntitiesCallBack([
      { id, suspended_at: active ? null : new Date() }
    ])
  }

  const mapToForm = (values = {}) => ({
    ...values,
    name: values.name ?? '',
    description: values.description ?? '',
    type_id: values.type_id ?? '',
    industry_id: values.industry_id ?? '',
    url: values.url ?? '',
    addresses: values.addresses?.map(a => ({
      name: a.name ?? '',
      unit: a.unit ?? '',
      street: a.street ?? '',
      locale: a.locale ?? '',
      city: a.city ?? '',
      region: a.region ?? '',
      country: a.country ?? '',
      postal: a.postal ?? '',
      lat: a.lat ?? '',
      long: a.long ?? ''
    })) ?? []
  })

  const makeInMemoryEntityRecord = (type) => {
    return {
      name: '',
      description: '',
      type_id: '',
      industry_id: '',
      phone: '',
      url: '',
      addresses: [
        {
          country: '',
          region: '',
          city: '',
          street: '',
          unit: '',
          name: 'generic',
          postal: '',
        },
      ],
      //
      service_ids: [],
      fullContacts: [],
      fullOwners: [],
      owners: [],
      contacts: [],
      _objectId: '',
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1_INITIAL_VALUES]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2_INITIAL_VALUES]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3_INITIAL_VALUES]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4_INITIAL_VALUES]: [],

      active: true,
    }
  }

  const upsertEntityCallback = (primaryRecord) => {
    setLoading(true)
    const returnPromise = new Promise((resolve, reject) => {
      upsertEntityRecordCallback(primaryRecord)
        .unwrap()
        .then((primaryResult) => {
          const id = primaryResult.data.id
            ? primaryResult.data.id
            : primaryResult.data.data.id

          const toSave = []

          const assetUpdateArray = difference(
            primaryRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3]?.map(
              (asset) => asset.id
            ),
            primaryRecord[
              ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3_INITIAL_VALUES
            ]?.map((asset) => asset.id)
          )?.map((assetId) => ({ id: assetId, entity_id: id })) ?? []

          if (assetUpdateArray.length) {
            toSave.push(bulkUpdateAssetsCallBack(assetUpdateArray))
          }

          const datazonePushPull = getPushPull(
            primaryRecord,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2_INITIAL_VALUES
          )
          const datazonePatch = []
          datazonePatch.push(
            ...datazonePushPull.push?.map(
              (datazoneId) => ({ id: datazoneId, entity_id_assign: id })
            ),
            ...datazonePushPull.pull?.map(
              (datazoneId) => ({ id: datazoneId, entity_id_unassign: id })
            )
          )

          if (datazonePatch.length) {
            toSave.push(bulkUpdateDatazones(datazonePatch))
          }

          const contractPushPull = getPushPull(
            primaryRecord,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4_INITIAL_VALUES
          )
          const contractPatch = []
          contractPatch.push(
            ...contractPushPull.push?.map(
              (contractId) => ({ id: contractId, entity_id_assign: id })
            ),
            ...contractPushPull.pull?.map(
              (contractId) => ({ id: contractId, entity_id_unassign: id })
            )
          )

          if (contractPatch.length) {
            toSave.push(bulkUpdateContracts(contractPatch))
          }

          Promise.all(toSave)
            .then(results => {
              const errors = results?.flatMap(r => r.data?.errors ?? [])
              errors.length === 0
                ? resolve({ id, type: RootObjectTypes.ENTITY })
                : reject(
                  'The following errors have occurred: ' +
                  errors.map(e => e.msg).join(", ")
                )
            })
            .catch(reject)
            .finally(() => {
              setLoading(false)
            })
        })
        .catch(reject)
    })

    return returnPromise
  }

  return {
    loadEntities,
    listEntityTypes,
    removeEntity,
    suspendEntity,
    bulkUpdateEntitiesCallBack,
    useGetOneFatEntityQuery,
    fetchAuxiliaryRecords,
    makeInMemoryEntityRecord,
    finalAuxiliaryRecords,
    upsertEntityCallback,
    mapToForm,
    loading
  }
}

export default useEntity
